import React from "react";
import { useTranslation } from "react-i18next";

type AvatarProps = {
  name?: string | null;
  email?: string | null;
  photoUrl?: string | null;
  fallbackUrl?: string | null;
  fallback?: string | null;
  size?: number;
  className?: string;
};

export function Avatar({
  name,
  email,
  photoUrl,
  fallbackUrl,
  fallback,
  size = 48,
  className
}: AvatarProps) {
  const { t } = useTranslation();
  const sanitizedPhotoUrl = React.useMemo(() => {
    if (!photoUrl) {
      return null;
    }
    try {
      const parsed = new URL(photoUrl);
      if (parsed.hostname === "minio") {
        return null;
      }
    } catch {
      // Ignore parse errors and fall back to remote URL.
      return null;
    }
    return photoUrl;
  }, [photoUrl]);

  const [src, setSrc] = React.useState<string | null>(sanitizedPhotoUrl ?? fallbackUrl ?? null);

  React.useEffect(() => {
    setSrc(sanitizedPhotoUrl ?? fallbackUrl ?? null);
  }, [sanitizedPhotoUrl, fallbackUrl]);

  const initials = React.useMemo(() => {
    if (fallback && fallback.trim().length > 0) {
      return fallback.trim().slice(0, 2).toUpperCase();
    }
    const basis = name ?? email ?? "?";
    return basis.trim().slice(0, 2).toUpperCase();
  }, [name, email, fallback]);

  const style: React.CSSProperties = {
    width: size,
    height: size
  };

  if (src) {
    return (
      <img
        src={src}
        alt={t("common.avatar.fallback_alt")}
        className={`inline-block rounded-full object-cover bg-slate-100 ${className ?? ""}`.trim()}
        style={style}
        referrerPolicy="no-referrer"
        onError={() => {
          if (fallbackUrl && src !== fallbackUrl) {
            setSrc(fallbackUrl);
          } else {
            setSrc(null);
          }
        }}
      />
    );
  }

  return (
    <span className={`inline-flex items-center justify-center rounded-full bg-slate-100 text-sm font-medium text-slate-600 ${className ?? ""}`.trim()} style={style} aria-hidden>
      {initials}
    </span>
  );
}
